import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, SliderCheckbox } from '@components/common';
import Actions from '@components/common/Actions';
import Tooltip from '@components/common/Tooltip';

import { uuid } from '@utils/common';

import { IUpdateOrganisationSettingsDTO } from '@interfaces/organisation/dto/organisationsDTO';
import { IOrganisationSetting } from '@interfaces/organisation/organisations';

import { IDataForEditInterface, tooltipContent } from './constants';

interface IGetColumns {
	handleUpdateOrganisationSettings: ({ vendorsSettings }: IUpdateOrganisationSettingsDTO) => void;
	openEditModal: () => void;
	setDataForEdit: (data: IDataForEditInterface) => void;
	openConfirmChangeStatusModal: () => void;
	organisationId?: string;
}

export const getColumns = ({
	handleUpdateOrganisationSettings,
	openEditModal,
	setDataForEdit,
	openConfirmChangeStatusModal,
	organisationId,
}: IGetColumns) => ([
	{
		header: 'Vendor',
		accessorKey: 'vendor',
		cell: ({ getValue }: CellContext<any, any>) => {
			const vendor = getValue();

			return <AdaptiveTableCell text={vendor?.title} />;
		},
	},
	{
		header: 'Status',
		accessorKey: 'status',
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const changeStatus = () => {
				if (original?.status) {
					setDataForEdit({
						vendor: original?.vendor,
						maxKpiSelectionInCharts: original?.maxKpiSelectionInCharts,
						status: !original?.status,
					});

					openConfirmChangeStatusModal();

					return;
				}

				if (!organisationId) return;

				handleUpdateOrganisationSettings({
					vendorsSettings: [{
						vendor: original.vendor.name,
						maxKpiSelectionInCharts: original?.maxKpiSelectionInCharts,
						status: !original.status,
					}],
					organisationId,
				});
			};

			const isSliderDisabled = original?.usedInSites && original?.status;

			return (
				<Tooltip
					content={tooltipContent}
					isDisabled={!isSliderDisabled}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-start',
							alignItems: 'center',
							paddingLeft: '15px',
							width: '50px',
						}}
					>
						<SliderCheckbox
							checked={original?.status}
							disabled={isSliderDisabled}
							id={uuid()}
							onChange={changeStatus}
						/>
					</div>
				</Tooltip>
			);
		},
	},
	{
		header: 'Max KPI Selection in charts',
		accessorKey: 'maxKpiSelectionInCharts',
		cell: ({ row: { original } }: CellContext<any, any>) => (
			<AdaptiveTableCell text={
				original?.maxKpiSelectionInCharts
					? original.maxKpiSelectionInCharts
					: 'No limit'
			}
			/>
		),
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IOrganisationSetting, any>) => {
			const isKpiAvailable = original?.isKpiAvailable;

			const editHandler = () => {
				setDataForEdit({
					vendor: original?.vendor,
					status: original?.status,
					maxKpiSelectionInCharts: original?.maxKpiSelectionInCharts,
				});

				openEditModal();
			};

			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						width: '110px',
					}}
				>
					<Actions
						editHandler={editHandler}
						isEdit={isKpiAvailable}
					/>
				</div>
			);
		},
	},
]);
